import React, {useState, useEffect, FunctionComponent} from "react";
import {
    Grid,
    Box,
    Typography,
    Container,
    Button,
} from "@material-ui/core";
import {makeStyles} from "@material-ui/core/styles";
import {useHistory, useLocation} from "react-router-dom";
import API from "../../helpers/apiHelpers";
import {userRole} from "../../interfaces";
import {useSnackbar} from "notistack";
import {colorPrimary} from "../../Constants/theme";
import PrescriptionModal from "../../Components/Modals/NewPrescription";
import {PrescriptionCard} from "../../Components";
import DocCard from "../../Components/DocCard";
import {useSelector} from "react-redux";
import {RootState, useAppDispatch} from "../../redux/store";
import {
    addNewPrescription, deletePrescriptionByID,
    getPrescriptions,
    patchPrescriptionByID, removePrescription,
    setPrescriptionList
} from "../../redux/appointment/appointment.actions";

interface AppointmentNotesProps {
    userType: userRole
}

const useStyle = makeStyles((theme) => ({
    wrapper: {
        marginTop: 90,
        marginBottom: 50,
    },
    leftContentWrapper: {
        width: "50%",
        height: "80%",
        marginTop: "20px",
        [theme.breakpoints.up("md")]: {
            marginLeft: "20px",
            marginTop: "0",
        },
    },
    cardContent: {
        display: "flex",
        flexDirection: "column",
        width: "260px",
        height: "425px",
        alignItems: "center",
        justifyContent: "space-between"
    },
    docPhoto: {
        display: "flex",
        justifyContent: "center"
    },
}));

const AppointmentPrescription: FunctionComponent<AppointmentNotesProps> = ({userType = "VET"}: AppointmentNotesProps) => {
    // const { state: { appointmentId } } = useLocation<{ appointmentId: number }>();
    // const { state: { redirectBack } } = useLocation<{ redirectBack: boolean }>();
    const redirectBack = false;

    const classes = useStyle();
    const history = useHistory();
    const [docFirstName, setDocFirstName] = useState("");
    const [docLastName, setDocLastName] = useState("");
    const [petId, setPetId] = useState();
    const [docEdu, setDocEdu] = useState("");
    const [docImage, setDocImage] = useState("");
    const [docType, setDocType] = useState("");
    const [modalVisibility, setModalVisibility] = useState(false);
    const [itemToEdit, setItemToEdit] = useState<any | undefined>();
    const {enqueueSnackbar} = useSnackbar();
    const appointment = useSelector((state: RootState) => state?.appointment.appointment);
    const prescriptionList = useSelector((state: RootState) => state?.appointment.prescriptions);
    const user = useSelector((state: RootState) => state?.user);
    const dispatch = useAppDispatch();

    useEffect(() => {
        // @ts-ignore
        setPetId(appointment.animal.id);
        API.getProfiles({userId: appointment.vetUserId})
            .then((profile) => {
                const {type, firstName, lastName, education, image} = profile[0];
                setDocFirstName(firstName || "");
                setDocLastName(lastName || "");
                setDocEdu(education || "");
                setDocImage(image || "");
                setDocType(type || "");
            });
        dispatch(getPrescriptions(appointment.id));
    }, []);

    const handleSubmit = (body) => {
        const list = [...prescriptionList];
        if (itemToEdit) {
            const itemToReplace = list.find(item => item.id === itemToEdit.id);
            list.splice(list.indexOf(itemToReplace), 1, {
                ...body,
                animalId: petId,
                appointmentId: appointment.id,
                id: itemToEdit.id
            });
            dispatch(setPrescriptionList(list));
            setItemToEdit(null);
        } else {
            list.push({
                ...body,
                animalId: petId,
                appointmentId: appointment.id
            });
            dispatch(setPrescriptionList(list));
        }
    };

    const fillRequest = async () => {
        try {
            const list = [...prescriptionList];
            for (const item of list) {
                if (item.id) {
                    await dispatch(patchPrescriptionByID(item.id, item));
                } else {
                    await dispatch(addNewPrescription(item));
                }
            }
            if (redirectBack) {
                history.push("/appointments");
            } else {
                user.role === "TECH" ?
                    history.push({
                        pathname: "/apointmentNotes",
                        state: {appointmentId: appointment.id, afterAppt: true}
                    }) :
                    history.push({
                        pathname: "/apointmentRate",
                        state: {appointmentId: appointment.id}
                    });
            }
        } catch (error) {
            enqueueSnackbar(error, {variant: "error", autoHideDuration: 3000});
        }
    };

    const deletePrescription = async () => {
        if (!itemToEdit) return;
        try {
            if (itemToEdit.id) {
                await dispatch(deletePrescriptionByID(itemToEdit.id));
            } else {
                dispatch(removePrescription(itemToEdit));
            }
            setItemToEdit(null);
        } catch (error) {
            enqueueSnackbar(error, {variant: "error"});
        }
    };

    return (
        <Box className={classes.wrapper}>
            <Container maxWidth="lg">
                <Grid container spacing={6}>
                    <Grid item sm={4} xs={12} justify="center">
                        <DocCard docType={docType} docFirstName={docFirstName} docLastName={docLastName} docEdu={docEdu}
                            docImage={docImage} setDocImage={setDocImage} scheduledDate={appointment.scheduledDate}
                            appointmentType={appointment.type} animalName={appointment?.animal?.name} dateOfBirth={appointment?.animal?.dateOfBirth}/>
                    </Grid>
                    <Grid item sm={8} xs={12}>
                        <Grid container>
                            <Grid item sm={12} xs={12}>
                                <Box marginBottom="33px">
                                    <Typography variant="h2">
                                        {appointment?.type.includes("ADVICE") ? "OTC" : "Prescriptions & OTC"}
                                    </Typography>
                                </Box>
                            </Grid>
                            {prescriptionList.length ?
                                <>
                                    <Grid item xs={12}>
                                        <Box marginBottom="24px">
                                            <Button
                                                variant="contained"
                                                style={{color: colorPrimary}}
                                                onClick={() => {
                                                    setItemToEdit(null);
                                                    setModalVisibility(true);
                                                }}
                                            >
                                                {appointment?.type.includes("ADVICE") ? "Add OTC" : "Add RX / OTC"}
                                            </Button>
                                        </Box>
                                    </Grid>
                                    {prescriptionList.map((item, idx) => {
                                        return <Grid key={idx} item md={8} sm={11} xs={12}>
                                            <PrescriptionCard item={item} edit={() => {
                                                setItemToEdit(item);
                                                setModalVisibility(true);
                                            }}/>
                                        </Grid>;
                                    })}
                                    <Grid item md={8} sm={11} xs={12}>
                                        <Box marginTop="70px" marginBottom="24px">
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                fullWidth
                                                onClick={fillRequest}
                                            >
                                                Submit & FIll Request
                                            </Button>
                                        </Box>
                                    </Grid>
                                </> :
                                <>
                                    <Grid item md={8} sm={10} xs={12}>
                                        <Box marginTop="0px" marginBottom="24px">
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                fullWidth
                                                onClick={() => setModalVisibility(true)}
                                            >
                                                {appointment?.type.includes("ADVICE") ? "Add OTC" : "Add RX / OTC"}
                                            </Button>
                                        </Box>
                                    </Grid>
                                    <Grid item md={8} sm={10} xs={12}>
                                        <Box marginTop="0px" marginBottom="24px">
                                            <Button
                                                variant="contained"
                                                fullWidth
                                                style={{color: colorPrimary}}
                                                onClick={() => {
                                                    if (redirectBack) {
                                                        history.push("/appointments");
                                                    } else {
                                                        user.role === "TECH" ?
                                                            history.push({
                                                                pathname: "/apointmentNotes",
                                                                state: {appointmentId: appointment.id}
                                                            }) :
                                                            history.push({
                                                                pathname: "/apointmentRate",
                                                                state: {appointmentId: appointment.id}
                                                            });
                                                    }
                                                }}
                                            >
                                                {appointment?.type.includes("ADVICE") ? "There is no OTC from this appointment " : "There is no RX / OTC from this appointment"}
                                            </Button>
                                        </Box>
                                    </Grid>
                                </>
                            }
                        </Grid>
                    </Grid>
                </Grid>
                <PrescriptionModal
                    isOpen={modalVisibility}
                    setOpenDialog={setModalVisibility}
                    handleSubmit={handleSubmit}
                    itemToEdit={itemToEdit}
                    setItemToEdit={setItemToEdit}
                    handleDelete={deletePrescription}
                    appointmentType={appointment?.type}
                />
            </Container>
        </Box>
    );
};

export default AppointmentPrescription;
